import Dexie, { type Table } from 'dexie'
import type { PhotoEnAttente } from './types'

/**
 * Base locale de l'appareil.
 *
 * Deux usages :
 *  - « attente » : la file d'attente sortante. Tout ce qui est créé hors ligne
 *    y est écrit d'abord, puis envoyé à la reconnexion. Rien n'est perdu si
 *    l'application se ferme ou si l'appareil redémarre.
 *  - « vignettes » : les aperçus déjà générés, pour que la grille de photos
 *    s'affiche instantanément et sans réseau.
 *  - « plans » : les fichiers PDF déjà ouverts, pour les rouvrir sans réseau.
 */
class BaseLocale extends Dexie {
  attente!: Table<PhotoEnAttente, string>
  vignettes!: Table<{ cle: string; blob: Blob }, string>
  plans!: Table<{ cle: string; octets: ArrayBuffer; conserve_le: number }, string>

  constructor() {
    super('chantiers-larente')
    this.version(1).stores({
      attente: 'client_uuid, chantier_id, cree_le',
      vignettes: 'cle'
    })
    // v2 : copie locale des plans PDF déjà ouverts (consultation hors ligne).
    this.version(2).stores({
      attente: 'client_uuid, chantier_id, cree_le',
      vignettes: 'cle',
      plans: 'cle, conserve_le'
    })
  }
}

export const base = new BaseLocale()

export async function mettreEnAttente(p: PhotoEnAttente): Promise<void> {
  await base.attente.put(p)
}

export async function fileDAttente(chantierId?: string): Promise<PhotoEnAttente[]> {
  const tout = await base.attente.orderBy('cree_le').toArray()
  return chantierId ? tout.filter((p) => p.chantier_id === chantierId) : tout
}

export async function retirerDeLaFile(clientUuid: string): Promise<void> {
  await base.attente.delete(clientUuid)
}

export async function marquerEchec(clientUuid: string, message: string): Promise<void> {
  const p = await base.attente.get(clientUuid)
  if (!p) return
  await base.attente.put({ ...p, essais: p.essais + 1, derniere_erreur: message })
}

export async function conserverVignette(cle: string, blob: Blob): Promise<void> {
  await base.vignettes.put({ cle, blob })
}

export async function lireVignette(cle: string): Promise<Blob | undefined> {
  return (await base.vignettes.get(cle))?.blob
}
